import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { LuBriefcase, LuCopy, LuIdCard, LuMail, LuMapPin, LuPhone, LuSiren, LuUser, LuUserPen } from 'react-icons/lu';
import Badge from '../../components/ui/Badge';
import Button from '../../components/ui/Button';
import GlassIcon from '../../components/ui/GlassIcon';
import Modal from '../../components/ui/Modal';
import { getInitials } from '../../components/ui/Avatar';
import { GLASS_CHIP } from '../../components/ui/glass';
import { useTranslation } from '../../i18n/LanguageContext';
import {
  PRIMARY_ADDRESS_HOME,
  PRIMARY_ADDRESS_WORK,
  isPrimaryAddress,
} from '../../utils/employeeAddress';
import { getFilterValueLabel } from './employeeColumns';
import { nationalityLabel } from './employeeForm/employeeFormOptions';

const EMPTY_VALUES = ['nan', 'nat', 'n/a', 'null', 'undefined'];

const display = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const str = String(value).trim();
  if (!str || EMPTY_VALUES.includes(str.toLowerCase())) return '—';
  return str;
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

const ageYears = (value) => {
  const d = toDate(value);
  if (!d) return null;
  const now = new Date();
  let years = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) years -= 1;
  return years > 0 && years < 130 ? years : null;
};

const useCopy = () => {
  const { t } = useTranslation();
  return async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(t('employees.panel.copied', { label }));
    } catch {
      toast.error(t('employees.panel.copyFailed'));
    }
  };
};

// Tek bilgi; copyable verildiğinde kopyalama düğmesi gösterir
const InfoItem = ({ label, value, hint, copyable, href, badge, multiline }) => {
  const { t } = useTranslation();
  const copy = useCopy();
  const isEmpty = !value || value === '—';
  return (
    <div className="tw-group tw-min-w-0 tw-rounded-xl tw-px-2.5 tw-py-2 tw-transition-colors hover:tw-bg-slate-900/[0.03] dark:hover:tw-bg-white/[0.03]">
      <p className="tw-flex tw-min-w-0 tw-items-center tw-gap-1.5 tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">
        <span className="tw-truncate">{label}</span>
        {badge}
      </p>
      <div className="tw-mt-0.5 tw-flex tw-min-w-0 tw-items-center tw-gap-1.5">
        {href && !isEmpty ? (
          <a href={href} title={value} className="tw-min-w-0 tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 tw-transition-colors hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300">
            {value}
          </a>
        ) : (
          <span
            title={isEmpty ? undefined : value}
            className={`tw-min-w-0 tw-text-sm tw-font-medium ${multiline ? 'tw-break-words' : 'tw-truncate'} ${isEmpty ? 'tw-text-slate-400 dark:tw-text-slate-500' : 'tw-text-slate-800 dark:tw-text-slate-100'}`}
          >
            {value}
          </span>
        )}
        {hint && <span className="tw-shrink-0 tw-text-xs tw-text-slate-400 dark:tw-text-slate-500">· {hint}</span>}
        {copyable && !isEmpty && (
          <button
            type="button"
            onClick={() => copy(value, label)}
            title={t('employees.panel.copyLabel', { label })}
            aria-label={t('employees.panel.copyLabel', { label })}
            className="tw-ml-auto tw-grid tw-h-6 tw-w-6 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-opacity-0 tw-transition hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-opacity-100 group-hover:tw-opacity-100 [@media(hover:none)]:tw-opacity-100 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
          >
            <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string,
  hint: PropTypes.string,
  copyable: PropTypes.bool,
  href: PropTypes.string,
  badge: PropTypes.node,
  multiline: PropTypes.bool,
};

const Section = ({ icon: Icon, title, twoColumns, children }) => (
  <section>
    <h3 className="tw-mb-1.5 tw-flex tw-items-center tw-gap-2 tw-px-2.5 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">
      <Icon aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
      {title}
    </h3>
    <div className={`tw-grid tw-gap-x-2 tw-gap-y-0.5 sm:tw-grid-cols-2 ${twoColumns ? '' : 'lg:tw-grid-cols-3'}`}>{children}</div>
  </section>
);

Section.propTypes = {
  icon: PropTypes.elementType.isRequired,
  title: PropTypes.string.isRequired,
  twoColumns: PropTypes.bool,
  children: PropTypes.node,
};

/** "Çalışan Kartı": everything about an employee at a glance (read only). */
const EmployeeCardModal = ({ employee, onClose, onEdit }) => {
  const { t, language, locale } = useTranslation();
  const copy = useCopy();
  // Profil sayfasından yüklenen fotoğraf Employees dokümanında profileImageUrl
  // alanında tutulur; yüklenemezse baş harflere geri dönülür
  const [photoFailed, setPhotoFailed] = useState(false);

  if (!employee) return null;

  const formatDate = (value) => {
    const d = toDate(value);
    return d ? d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit', year: 'numeric' }) : display(value);
  };
  const yesNo = (value) => {
    if (value === true || value === 'Yes') return t('employees.yes');
    if (value === false || value === 'No') return t('employees.no');
    return display(value);
  };
  const optionText = (key, value) => (display(value) === '—' ? '—' : getFilterValueLabel(t, key, value));
  const primaryBadge = (addressKey) => (isPrimaryAddress(employee, addressKey)
    ? <Badge tone="info" className="tw-text-[10px]">{t('employees.table.primaryAddress')}</Badge>
    : null);

  const isActive = Number(employee.Active) === 1;
  const age = ageYears(employee.birthday);
  const photoUrl = !photoFailed && employee.profileImageUrl ? employee.profileImageUrl : null;
  const fullName = display(`${employee.eName || ''} ${employee.eSurname || ''}`.trim());
  const subtitle = display(employee.role) !== '—' ? employee.role : display(employee.profession);
  const heroChips = [
    display(employee.departman),
    optionText('workType', employee.workType),
    display(employee.authorization),
  ].filter((value) => value !== '—');

  const contacts = [
    { key: 'mail', icon: LuMail, label: t('employees.columns.eMail'), value: display(employee.eMail), href: `mailto:${employee.eMail}`, copyable: true },
    { key: 'phone', icon: LuPhone, label: t('employees.columns.phone'), value: display(employee.phone), href: `tel:${employee.phone}`, copyable: true },
    { key: 'emergency', icon: LuSiren, label: t('employees.form.fields.emergencyPhone'), value: display(employee.emergencyPhone), href: `tel:${employee.emergencyPhone}` },
  ];

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="form"
      title={t('employees.modals.card.title')}
      icon={(
        <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="xs">
          <LuIdCard aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
      )}
      closeLabel={t('common.close')}
      lang={language}
      bodyClassName="tw-p-0"
      footer={(
        <>
          <Button variant="ghost" onClick={() => onClose()}>{t('common.close')}</Button>
          {onEdit && (
            <Button variant="primary" onClick={onEdit}>
              <LuUserPen aria-hidden="true" className="tw-h-4 tw-w-4" />
              {t('employees.modals.card.edit')}
            </Button>
          )}
        </>
      )}
    >
      <div className="md:tw-grid md:tw-grid-cols-[17rem_minmax(0,1fr)]">
        {/* Profile and contact */}
        <aside className="tw-space-y-4 tw-border-b tw-border-slate-200/80 tw-bg-slate-50/70 tw-p-5 md:tw-border-b-0 md:tw-border-r dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
          <div className="tw-flex tw-items-center tw-gap-4 md:tw-flex-col md:tw-text-center">
            {photoUrl ? (
              <img
                src={photoUrl}
                alt={t('employees.modals.card.photoAlt', { name: fullName })}
                onError={() => setPhotoFailed(true)}
                className="tw-h-16 tw-w-16 tw-shrink-0 tw-rounded-full tw-object-cover tw-ring-4 tw-ring-white tw-shadow-lg md:tw-h-20 md:tw-w-20 dark:tw-ring-white/10"
              />
            ) : (
              <GlassIcon gradient="tw-from-teal-400 tw-to-cyan-600 tw-shadow-teal-500/30" size="avatarXl" className="md:tw-h-20 md:tw-w-20 md:tw-text-2xl">
                {getInitials(employee.eName, employee.eSurname)}
              </GlassIcon>
            )}
            <div className="tw-min-w-0">
              <h2 className="tw-text-lg tw-font-semibold tw-leading-tight tw-text-slate-900 dark:tw-text-white">{fullName}</h2>
              {subtitle !== '—' && <p className="tw-mt-1 tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{subtitle}</p>}
              <div className="tw-mt-2.5 tw-flex tw-flex-wrap tw-gap-1.5 md:tw-justify-center">
                <Badge tone={isActive ? 'success' : 'neutral'}>
                  <span aria-hidden="true" className={`tw-h-1.5 tw-w-1.5 tw-rounded-full ${isActive ? 'tw-bg-emerald-500' : 'tw-bg-slate-400'}`} />
                  {isActive ? t('employees.status.active') : t('employees.status.inactive')}
                </Badge>
                {heroChips.map((chip) => (
                  <Badge key={chip} tone="neutral">{chip}</Badge>
                ))}
              </div>
            </div>
          </div>

          <ul className="tw-space-y-1.5">
            {contacts.map((item) => {
              const Icon = item.icon;
              const empty = item.value === '—';
              return (
                <li key={item.key} className={`tw-group tw-flex tw-items-center tw-gap-3 tw-rounded-xl tw-px-3 tw-py-2 ${GLASS_CHIP}`}>
                  <Icon aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
                  <div className="tw-min-w-0 tw-flex-1">
                    <p className="tw-text-[11px] tw-font-medium tw-text-slate-400 dark:tw-text-slate-500">{item.label}</p>
                    {empty ? (
                      <p className="tw-text-sm tw-text-slate-400 dark:tw-text-slate-500">—</p>
                    ) : (
                      <a href={item.href} title={item.value} className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 hover:tw-text-teal-700 dark:tw-text-slate-100 dark:hover:tw-text-teal-300">
                        {item.value}
                      </a>
                    )}
                  </div>
                  {item.copyable && !empty && (
                    <button
                      type="button"
                      onClick={() => copy(item.value, item.label)}
                      title={t('employees.panel.copyLabel', { label: item.label })}
                      aria-label={t('employees.panel.copyLabel', { label: item.label })}
                      className="tw-grid tw-h-7 tw-w-7 tw-shrink-0 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-opacity-0 tw-transition hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-700 focus-visible:tw-opacity-100 group-hover:tw-opacity-100 [@media(hover:none)]:tw-opacity-100 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                    >
                      <LuCopy aria-hidden="true" className="tw-h-3.5 tw-w-3.5" />
                    </button>
                  )}
                </li>
              );
            })}
          </ul>
        </aside>

        {/* Details */}
        <div className="tw-space-y-4 tw-p-3 sm:tw-p-4">
          <Section icon={LuUser} title={t('employees.modals.card.personal')}>
            <InfoItem label={t('employees.form.fields.ID')} value={display(employee.ID)} />
            <InfoItem label={t('employees.form.fields.tcNumber')} value={display(employee.tcNumber)} copyable />
            <InfoItem
              label={t('employees.form.fields.birthday')}
              value={formatDate(employee.birthday)}
              hint={age ? t('employees.modals.card.age', { age }) : undefined}
            />
            <InfoItem label={t('employees.form.fields.nationality')} value={display(employee.nationality) === '—' ? '—' : nationalityLabel(t, employee.nationality)} />
            <InfoItem label={t('employees.form.fields.educationInfo')} value={optionText('educationInfo', employee.educationInfo)} />
            <InfoItem label={t('employees.form.fields.languageInfo')} value={display(employee.languageInfo)} />
            <InfoItem label={t('employees.columns.isHandicappedPerson')} value={yesNo(employee.isHandicappedPerson)} />
            <InfoItem label={t('employees.columns.isRetired')} value={yesNo(employee.isRetired)} />
            <InfoItem label={t('employees.columns.isIntern')} value={yesNo(employee.isIntern)} />
          </Section>

          <Section icon={LuBriefcase} title={t('employees.modals.card.work')}>
            <InfoItem label={t('employees.form.fields.departman')} value={display(employee.departman)} />
            <InfoItem label={t('employees.form.fields.profession')} value={display(employee.profession)} />
            <InfoItem label={t('employees.form.fields.role')} value={display(employee.role)} />
            <InfoItem label={t('employees.form.fields.workType')} value={optionText('workType', employee.workType)} />
            <InfoItem label={t('employees.form.fields.startDate')} value={formatDate(employee.startDate)} />
            <InfoItem label={t('employees.form.fields.workCity')} value={display(employee.workCity)} />
            <InfoItem label={t('employees.columns.annualPermit')} value={display(employee.annualPermit)} />
            <InfoItem label={t('employees.form.fields.authorization')} value={display(employee.authorization)} />
          </Section>

          {/* Çalışanın profilinden seçtiği ana adres rozetle işaretlenir */}
          <Section icon={LuMapPin} title={t('employees.modals.card.addresses')} twoColumns>
            <InfoItem label={t('employees.form.fields.address')} value={display(employee.address)} badge={primaryBadge(PRIMARY_ADDRESS_HOME)} multiline />
            <InfoItem label={t('employees.form.fields.workAddress')} value={display(employee.workAddress)} badge={primaryBadge(PRIMARY_ADDRESS_WORK)} multiline />
          </Section>
        </div>
      </div>
    </Modal>
  );
};

EmployeeCardModal.propTypes = {
  employee: PropTypes.object,
  onClose: PropTypes.func.isRequired,
  onEdit: PropTypes.func,
};

export default EmployeeCardModal;
